import { Bell, Moon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  useNotificationPreferences,
  NOTIF_CHANNEL_LABELS,
  type NotifChannel,
} from "@/lib/notifications/usePreferences";

const CHANNEL_VALUES: NotifChannel[] = ["instant", "every_15m", "hourly", "daily_digest", "off"];

const ROW_LABELS: { key: keyof ReturnType<typeof rowMap>; label: string; help: string }[] = [
  { key: "matches_channel", label: "Matches", help: "New mutual matches" },
  { key: "messages_channel", label: "Messages", help: "Direct messages from people you matched with" },
  { key: "community_channel", label: "Community", help: "Activity in community rooms" },
  { key: "events_channel", label: "Events", help: "RSVPs, reminders, check-ins" },
  { key: "broadcasts_channel", label: "Announcements", help: "Updates from the Wavlynq team" },
  { key: "raela_channel", label: "RAELA", help: "Co-pilot suggestions and gentle nudges" },
];

const rowMap = () => ({
  matches_channel: "" as NotifChannel,
  messages_channel: "" as NotifChannel,
  community_channel: "" as NotifChannel,
  events_channel: "" as NotifChannel,
  broadcasts_channel: "" as NotifChannel,
  raela_channel: "" as NotifChannel,
});

const NotificationPreferencesPanel = () => {
  const { prefs, loading, update } = useNotificationPreferences();

  if (loading || !prefs) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const setChannel = async (key: keyof ReturnType<typeof rowMap>, value: NotifChannel) => {
    const { error } = await update({ [key]: value });
    if (error) toast.error(error);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Per-channel batching</h3>
        </div>
        <div className="space-y-2">
          {ROW_LABELS.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{row.label}</p>
                <p className="text-xs text-muted-foreground">{row.help}</p>
              </div>
              <Select
                value={prefs[row.key]}
                onValueChange={(v) => setChannel(row.key, v as NotifChannel)}
              >
                <SelectTrigger className="w-36 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_VALUES.map((v) => (
                    <SelectItem key={v} value={v}>
                      {NOTIF_CHANNEL_LABELS[v]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Moon className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Quiet hours</h3>
        </div>
        <div className="space-y-3 rounded-xl border border-border bg-card p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="quiet-start">From</Label>
              <Input
                id="quiet-start"
                type="time"
                value={prefs.quiet_start ?? ""}
                onChange={(e) => update({ quiet_start: e.target.value || null })}
              />
            </div>
            <div>
              <Label htmlFor="quiet-end">Until</Label>
              <Input
                id="quiet-end"
                type="time"
                value={prefs.quiet_end ?? ""}
                onChange={(e) => update({ quiet_end: e.target.value || null })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <div>
              <p className="text-sm font-medium">Always allow urgent</p>
              <p className="text-xs text-muted-foreground">
                Safety alerts still get through during quiet hours.
              </p>
            </div>
            <Switch
              checked={prefs.quiet_allow_urgent}
              onCheckedChange={(v) => update({ quiet_allow_urgent: v })}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Quiet hours use your timezone:{" "}
            <span className="font-medium">{prefs.timezone}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferencesPanel;
